'use client';

import { useMemo } from 'react';
import type { ShopperBasketsV2, ShopperSearch } from '@/scapi';
import { useBasket } from '@/providers/basket';
import { findBasketLineForSearchHit, isPlpSearchHitBundleOrSet } from '@/lib/cart/plp-basket-line';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

/**
 * Resolves a basket line for a PLP / carousel tile. Uses `useBasketData` only; the grid also calls
 * `useBasket()` so `current.productItems` is hydrated and matches on first visit.
 * @see Figma 703-2248 — "Quantità" label + vertical stepper on horizontal card
 *
 * The tile gates rendering with `useBasketLineReadiness` and React Router `Suspense` + `Await` in
 * `ProductTileCustom` until the basket is hydrated, not with this hook.
 */
export function useProductTileBasketLine(
    product: SearchHit,
    selectedColorValue: string | null | undefined
): {
    line: ShopperBasketsV2.schemas['ProductItem'] | undefined;
    isBundleOrSet: boolean;
} {
    const basket = useBasket();
    const productItems = basket?.productItems;

    // Product sets skip quantity matching (configure on PDP). Bundles are ATC-able as parent id.
    const isBundleOrSet = isPlpSearchHitBundleOrSet(product);

    const line = useMemo(() => {
        if (isBundleOrSet || !productItems?.length) {
            return undefined;
        }
        return findBasketLineForSearchHit(product, selectedColorValue, productItems);
    }, [isBundleOrSet, product, productItems, selectedColorValue]);

    return { line, isBundleOrSet };
}
